import patternOfLifeEndpoints from './endpoints/patternOfLife.endpoints';

/**
 * Get the Pattern of Life model for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @returns {Promise<Object|null>} The POL model with significant locations and routine matrix.
 */
export async function getPatternOfLife(alienId) {
  try {
    const response = await patternOfLifeEndpoints.get(alienId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getPatternOfLife] No POL model found for alien ${alienId}`);
      return null;
    }
    console.error('[getPatternOfLife] Error:', error);
    return null;
  }
}

/**
 * Trigger a rebuild of the Pattern of Life model for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @returns {Promise<Object|null>} The newly built POL model.
 */
export async function rebuildPatternOfLife(alienId) {
  try {
    const response = await patternOfLifeEndpoints.rebuild(alienId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[rebuildPatternOfLife] Error:', error);
    return null;
  }
}

/**
 * Check if current location deviates from expected routine.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [latitude] - Optional current latitude.
 * @param {number} [longitude] - Optional current longitude.
 * @returns {Promise<Object|null>} Deviation analysis with score and alert level.
 */
export async function checkDeviation(alienId, latitude, longitude) {
  try {
    const params = {};
    if (latitude !== undefined && latitude !== null) params.latitude = latitude;
    if (longitude !== undefined && longitude !== null) params.longitude = longitude;

    const response = await patternOfLifeEndpoints.checkDeviation(alienId, { params });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[checkDeviation] No POL model found for alien ${alienId}`);
      return null;
    }
    console.error('[checkDeviation] Error:', error);
    return null;
  }
}

/**
 * Detect anomaly using the multi-model ensemble.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [latitude] - Optional current latitude.
 * @param {number} [longitude] - Optional current longitude.
 * @param {Date} [timestamp] - Optional timestamp for the check.
 * @returns {Promise<Object|null>} Anomaly detection response with ensemble scores.
 */
export async function detectAnomaly(alienId, latitude, longitude, timestamp) {
  try {
    const params = {};
    if (latitude !== undefined && latitude !== null) params.latitude = latitude;
    if (longitude !== undefined && longitude !== null) params.longitude = longitude;
    if (timestamp) params.timestamp = timestamp.toISOString();

    const response = await patternOfLifeEndpoints.detectAnomaly(alienId, { params });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[detectAnomaly] No ensemble model found for alien ${alienId}`);
      return null;
    }
    console.error('[detectAnomaly] Error:', error);
    return null;
  }
}

/**
 * Train the anomaly detection ensemble for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @returns {Promise<Object|null>} Training result with status and sample count.
 */
export async function trainAnomalyEnsemble(alienId) {
  try {
    const response = await patternOfLifeEndpoints.trainAnomaly(alienId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[trainAnomalyEnsemble] Error:', error);
    return null;
  }
}

/**
 * Get anomaly detection history for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [limit=50] - Maximum number of records to return.
 * @returns {Promise<Object|null>} Anomaly history response.
 */
export async function getAnomalyHistory(alienId, limit = 50) {
  try {
    const response = await patternOfLifeEndpoints.getAnomalyHistory(alienId, { params: { limit } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getAnomalyHistory] No history found for alien ${alienId}`);
      return null;
    }
    console.error('[getAnomalyHistory] Error:', error);
    return null;
  }
}
